import { supabase } from '../lib/supabase.js'

/**
 * Service for handling subscription-related operations
 */
export const subscriptionService = {
  
  /**
   * Get user's subscription information from their profile
   * @param {string} userId - User's ID
   * @returns {Promise<Object|null>} Subscription data or null
   */
  async getUserSubscription(userId) {
    try {
      if (!userId) {
        return null
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_status, username, full_name')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching user subscription:', error)
        return null
      }

      const tier = data.subscription_tier || 'None'
      const status = data.subscription_status || 'none'
      const hasActiveTier = !['New User', 'None'].includes(tier)

      return {
        subscriptionTier: tier,
        subscriptionStatus: status,
        username: data.username,
        fullName: data.full_name,
        hasActiveSubscription: status === 'active' && hasActiveTier
      }
    } catch (error) {
      console.error('Error in getUserSubscription:', error)
      return null
    }
  },

  /**
   * Check if user has access to premium content
   * @param {string} userId - User's ID
   * @returns {Promise<boolean>} True if user has premium access
   */
  async hasAccessToContent(userId) {
    const subscription = await this.getUserSubscription(userId)
    
    if (!subscription) {
      return false
    }

    // Check if user has any active subscription tier
    return subscription.hasActiveSubscription
  },

  /**
   * Get subscription tier requirements for different content types
   * @param {string} contentType - Type of content (e.g., 'movie', 'tv_show', 'premium')
   * @returns {string} Required subscription tier
   */
  getRequiredTier(contentType = 'basic') {
    const requirements = {
      'free': 'None',
      'basic': 'Basic',
      'standard': 'Standard', 
      'premium': 'Premium',
      'movie': 'Basic',
      'tv_show': 'Basic',
      'exclusive': 'Premium'
    }
    
    return requirements[contentType] || 'Basic'
  },

  /**
   * Check if user's subscription tier meets the requirement
   * @param {string} userTier - User's current subscription tier
   * @param {string} requiredTier - Required subscription tier
   * @returns {boolean} True if user has sufficient access
   */
  hasRequiredTier(userTier, requiredTier) {
    const tierHierarchy = {
      'None': 0,
      'Basic': 1,
      'Standard': 2,
      'Premium': 3
    }
    
    const userLevel = tierHierarchy[userTier] || 0
    const requiredLevel = tierHierarchy[requiredTier] || 1
    
    return userLevel >= requiredLevel
  },

  /**
   * Get subscription upgrade suggestions
   * @param {string} currentTier - User's current subscription tier
   * @param {string} requiredTier - Required subscription tier
   * @returns {Object} Upgrade suggestions
   */
  getUpgradeSuggestions(currentTier = 'None', requiredTier = 'Basic') {
    const benefits = {
      'Basic': [
        'Access to standard content library',
        'HD streaming quality',
        'Watch on 2 devices simultaneously'
      ],
      'Standard': [
        'Everything in Basic',
        'Access to exclusive series',
        '4K streaming quality',
        'Watch on 4 devices simultaneously',
        'Offline downloads'
      ],
      'Premium': [
        'Everything in Standard',
        'Early access to new releases',
        'Premium exclusive content',
        'Watch on unlimited devices',
        'Priority customer support'
      ]
    }

    return {
      currentTier,
      requiredTier,
      needsUpgrade: !this.hasRequiredTier(currentTier, requiredTier),
      benefits: benefits[requiredTier] || benefits['Basic'],
      upgradeUrl: '/pricing'
    }
  }
}

export default subscriptionService